import { StudentProxyType } from '@peekapak/types/DataProtocolTypes';
import { Lesson } from '@peekapak/types/LessonPlanTypes';
import { AttendanceLesson, AttendanceSubmissionPayload } from '../BackendInterface';

export type AttendanceStatus = 'present' | 'absent';

export interface AttendanceDraft {
  selectedLessons: AttendanceLesson[];
  attendanceStatuses: Record<string, AttendanceStatus>;
  attendanceDate: string;
  isAttendanceDateLocked: boolean;
}

const ATTENDANCE_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Initialises an attendance status map with every student defaulting to 'present'.
 * The map is keyed by each student's `userId`.
 */
export function initializeAttendanceStatuses(students: StudentProxyType[]): Record<string, AttendanceStatus> {
  return students.reduce<Record<string, AttendanceStatus>>((acc, student) => {
    acc[student.userId] = 'present';
    return acc;
  }, {});
}

/**
 * Counts how many students are present and absent in the given status map.
 */
export function countAttendance(statuses: Record<string, AttendanceStatus>): {
  present: number;
  absent: number;
} {
  return Object.values(statuses).reduce(
    (acc, status) => {
      if (status === 'present') {
        acc.present += 1;
      } else {
        acc.absent += 1;
      }
      return acc;
    },
    { present: 0, absent: 0 },
  );
}

/**
 * Returns the localforage key used to cache an attendance draft for a specific classroom.
 */
export function buildAttendanceCacheKey(classroomId: string): string {
  return `takween-attendance-draft-${classroomId}`;
}

/**
 * Converts a Set of selected lessonIds into AttendanceLesson objects using the loaded lesson list.
 * Lessons not found in the list are silently skipped.
 * lessonNumber is idx + 1 in the rendered lesson order.
 */
export function buildLessonObjects(selectedLessons: Set<string>, lessons: Lesson[]): AttendanceLesson[] {
  return lessons.reduce<AttendanceLesson[]>((acc, lesson, idx) => {
    if (!selectedLessons.has(lesson.lessonId)) {
      return acc;
    }

    acc.push({
      lessonId: lesson.lessonId,
      lessonNumber: idx + 1,
      title: lesson.title,
    });

    return acc;
  }, []);
}

/**
 * Assembles the payload object for the submitAttendance API call.
 */
export function buildSubmissionPayload(
  classroomId: string,
  unitId: string,
  attendanceDate: string,
  selectedLessons: Set<string>,
  attendanceStatuses: Record<string, AttendanceStatus>,
  lessons: Lesson[],
  courseName: string,
): AttendanceSubmissionPayload {
  return {
    classroomId,
    unitId,
    attendanceDate,
    lessons: buildLessonObjects(selectedLessons, lessons),
    attendanceStatuses,
    submittedAt: Date.now(),
    courseName,
  };
}

export function getTodayAttendanceDate(referenceDate: Date = new Date()): string {
  return getCanonicalAttendanceDateFromDate(referenceDate);
}

export function getCanonicalAttendanceDateFromDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function formatAttendanceDateForLocale(attendanceDate: string, locale: string = getUserLocale()): string {
  const date = parseCanonicalAttendanceDate(attendanceDate);
  if (!date) {
    return attendanceDate;
  }

  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC',
  }).format(date);
}

export function formatAttendanceDateForControl(attendanceDate: string, locale: string = getUserLocale()): string {
  const date = parseCanonicalAttendanceDate(attendanceDate);
  if (!date) {
    return attendanceDate;
  }

  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

export function parseAttendanceDateInput(input: string, locale: string = getUserLocale()): string | null {
  const trimmed = input.trim();
  if (!trimmed) {
    return null;
  }

  if (ATTENDANCE_DATE_REGEX.test(trimmed) && parseCanonicalAttendanceDate(trimmed)) {
    return trimmed;
  }

  const sampleDate = new Date(Date.UTC(2006, 10, 22));
  const parts = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC',
  }).formatToParts(sampleDate);

  const order = parts.filter((part) => part.type !== 'literal').map((part) => part.type);
  const numbers = trimmed.match(/\d+/g);

  if (!numbers || numbers.length !== 3 || order.length !== 3) {
    return null;
  }

  const valueByType = order.reduce<Record<string, string>>((acc, type, index) => {
    acc[type] = numbers[index];
    return acc;
  }, {});

  const year = valueByType.year?.padStart(4, '0');
  const month = valueByType.month?.padStart(2, '0');
  const day = valueByType.day?.padStart(2, '0');
  const canonicalDate = year && month && day ? `${year}-${month}-${day}` : '';

  return parseCanonicalAttendanceDate(canonicalDate) ? canonicalDate : null;
}

export function parseCanonicalAttendanceDate(attendanceDate: string): Date | null {
  if (!ATTENDANCE_DATE_REGEX.test(attendanceDate)) {
    return null;
  }

  const [yearString, monthString, dayString] = attendanceDate.split('-');
  const year = Number(yearString);
  const month = Number(monthString);
  const day = Number(dayString);
  const date = new Date(Date.UTC(year, month - 1, day));

  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    return null;
  }

  return date;
}

export function parseCanonicalAttendanceDateAsLocalDate(attendanceDate: string): Date | null {
  if (!ATTENDANCE_DATE_REGEX.test(attendanceDate)) {
    return null;
  }

  const [yearString, monthString, dayString] = attendanceDate.split('-');
  const year = Number(yearString);
  const month = Number(monthString);
  const day = Number(dayString);
  const date = new Date(year, month - 1, day);

  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }

  return date;
}

function getUserLocale(): string {
  if (typeof window === 'undefined') {
    return 'en-US';
  }

  return window.navigator.language || 'en-US';
}
